import { makeAutoObservable } from 'mobx'
import Loader from 'reusable/mobx-helpers/Loader'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { AutocompleteOption } from 'reusable/mui/auto-complete/shared/AutocompleteOption'
import { ServiceRateTypeEnum, serviceRateTypeEnums } from '@gap/backend/app-services/services/models/ServiceRateTypesDto'
import { ServiceDto } from '@gap/backend/app-services/services/models/ServiceDto'
import { ServiceCategoryTypeEnum, serviceCategoryTypeEnums } from '@gap/backend/app-services/services/models/ServiceCategoryTypesDto'
import { snackbarError, snackbarSuccess } from 'reusable/mui/snackbar/snackbar'
import { apiClient } from 'api/trpc/trpc-client'
import { ServiceAdvisorCreateDto, ServiceAdvisorCreateDtoSchema, ServiceCreateDto, ServiceGapCreateDto, ServiceGapCreateDtoSchema } from '@gap/backend/app-services/services/models/ServiceCreateDto'
import { notNull } from '@gap/utils/utils'
import { ServiceAdvisorUpdateDtoSchema, ServiceGapUpdateDtoSchema } from '@gap/backend/app-services/services/models/ServiceUpdateDto'
import { DropResult } from '@hello-pangea/dnd'
import { CurrencyDto } from '@gap/backend/app-services/location/models/CurrencyDto'
import { ServiceOption } from './service-item/ServiceAutocomplete'
import { FeatureEnum } from '@gap/backend/app-services/feature/models/FeatureEnum'
import { ulid } from 'ulid'
import { defaultStartingPriceRate } from './serviceConstants'
import { currencyList } from '@gap/utils/country/countryList'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'

type ServiceList = Array<{ category: ServiceCategoryTypeEnum, services: ServiceDto[] }>

export class ServiceStore {
   loader = new Loader(false)
   serviceBeingEdited: ServiceDto | null
   serviceDrawer = new ModalState()
   serviceSaveLoader = new Loader(false)
   serviceDeleteLoader = new Loader(false)
   drawerTitle = ''
   serviceAndCategoryList: ServiceList
   lastAddedOrEditedServiceId: string | undefined
   selectedServiceId: string | null = null
   selectedGapServiceId: string | null = null
   inputsChanged = false
   reallyDelete = false
   currencies: CurrencyDto[]
   instantiatedSource: FeatureEnum = 'services'
   howItWorksKeySuffix = ulid()
   sp

   fields = {
      name: '',
      description: '',
      howItWorks: '',
      category: '',
      startingPrice: '',
      startingPriceRate: '',
   }

   constructor(sp: { orgGuardStore: OrgGuardStore }) {
      this.sp = sp
      this.currencies = currencyList
      makeAutoObservable(this)
   }

   get currentOrg() {
      return this.sp.orgGuardStore.org
   }

   get orgCurrency() {
      return this.currencies.find(({ code }) => this.currentOrg?.currencyCode === code)
   }

   get formHandler() {
      return formHandler(this.fields, {
         name: { type: 'string', onValueChange: this.serviceNameFieldChangeHandler },
         description: { type: 'string', onValueChange: this.onInputChange },
         howItWorks: { type: 'rich-text-editor', onValueChange: this.onInputChange },
         category: { type: 'string', onValueChange: this.onInputChange },
         startingPrice: { type: 'number', min: 0, onValueChange: this.onInputChange },
         startingPriceRate: { type: 'string', onValueChange: this.onInputChange }
      } satisfies FormHandlerConfig)
   }

   get serviceRateOptions() {
      return serviceRateTypeEnums.map(service => ({ id: service, label: service })) as AutocompleteOption[]
   }

   get serviceCategoryOptions() {
      return serviceCategoryTypeEnums.map(service => ({ id: service, label: service })) as AutocompleteOption[]
   }

   get servicesAndCategoriesForCurrentOrg(): ServiceList {
      return (this.serviceAndCategoryList || []).reduce<ServiceList>((list, { category, services }) => {
         const currentOrgServices = services.filter((service) => service.orgId === this.currentOrg?.orgId)
         return [
            ...list,
            ...(currentOrgServices.length ? [{
               category,
               services: currentOrgServices,
            }] : []),
         ];
      }, []);
   }

   get services() {
      return this.serviceAndCategoryList?.length
         ? this.serviceAndCategoryList.flatMap((s) => s.services)
         : []
   }

   get closeDrawerText() {
      return this.selectedServiceId ? 'Discard changes' : 'Cancel'
   }

   get displayCancelOrDiscard() {
      return (this.selectedServiceId && this.inputsChanged) || !this.selectedServiceId
   }

   get currentOrgHasServices() {
      return this.servicesAndCategoriesForCurrentOrg.flatMap(({ services }) => services).length
   }

   get parsedFieldValues(): ServiceCreateDto {
      const base: ServiceGapCreateDto = {
         name: this.fields.name,
         description: this.fields.description || undefined,
         howItWorks: this.fields.howItWorks || undefined,
         category: this.fields.category as ServiceCategoryTypeEnum,
      }

      if (this.currentOrg?.orgType === 'advisor') {
         const withPricing: ServiceAdvisorCreateDto = {
            ...base,
            pricing: {
               startingPrice: Number(this.fields.startingPrice),
               startingPriceRate: this.fields.startingPriceRate as ServiceRateTypeEnum,
               currency: notNull(this.orgCurrency),
            },
         }

         return withPricing
      }

      return base
   }

   get currencySymbolForSelectedService() {
      return this.selectedServiceId
         && !this.inputsChanged
         && this.services
            .find(({ id }) => id === this.selectedServiceId)
            ?.pricing
            ?.currency.symbol
         || this.orgCurrency?.symbol
         || '$'
   }

   get serviceNameLabel() {
      if (this.currentOrg?.orgType === 'gap') {
         return this.selectedServiceId ? 'Service' : 'Create a service'
      }

      switch (this.instantiatedSource) {
         case 'proposals':
            return "Create a service"
         case 'services':
            return this.selectedServiceId ? 'Service' : 'Find or create a service'
      }
      return 'Find or create a service'
   }

   onInputChange = () => {
      this.inputsChanged = true
   }

   resetFields = () => {
      this.fields = {
         name: '',
         description: '',
         howItWorks: '',
         category: '',
         startingPrice: '',
         startingPriceRate: ''
      }
   }

   openServiceDrawer = (action: 'add' | 'edit') => {
      switch (action) {
         case 'add': {
            const drawerTitle = 'Add service'
            if (this.drawerTitle !== drawerTitle) {
               this.resetFields()
            }
            this.drawerTitle = 'Add service'
            break
         }
         case 'edit': {
            // Some logic to check if we are editing the same service as the last edit. If not, resetFields
            this.drawerTitle = 'Edit service'
            const service = this.services.find(service => service.id === this.selectedServiceId)
            if (!service) {
               snackbarError('Service not found')
               return
            }

            this.serviceBeingEdited = service

            this.resetFields()
            this.fillFieldsForService(service)
            break
         }
      }

      this.serviceDrawer.open()
   }

   addServiceWithStartingValue = (startingValue: string) => {
      this.openServiceDrawer('add')
      this.fields.name = startingValue
      this.inputsChanged = true
   }

   addService = async () => {
      this.openServiceDrawer('add')
   }

   fillFieldsForService = (service: ServiceDto, options?: { init?: boolean }) => {
      this.fields.name = service.name
      this.fields.description = service.description || ''
      this.fields.howItWorks = service.howItWorks || ''
      this.fields.category = service.category
      if (options?.init) {
         this.fields.startingPriceRate = defaultStartingPriceRate
      }
      if (service.pricing) {
         this.fields.startingPrice = String(service.pricing.startingPrice)
         this.fields.startingPriceRate = service.pricing.startingPriceRate
      }
      this.howItWorksKeySuffix = ulid() // change the key to force the RTE to re-render
   }

   editService = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      this.selectedServiceId = event.currentTarget.getAttribute("data-service-id")
      if (!this.selectedServiceId) {
         snackbarError('Service not found')
         return
      }
      this.openServiceDrawer('edit')
   }

   closeServiceDrawer = ({ clear }: { clear: boolean } = { clear: false }) => {
      this.serviceBeingEdited = null
      this.selectedServiceId = null
      this.inputsChanged = false
      this.reallyDelete = false
      this.serviceDrawer.close()
      if (clear) {
         this.resetFields()
      }
   }

   closeServiceDrawerButtonHandler = () => {
      this.closeServiceDrawer()
   }

   closeServiceDrawerAndClearForm = () => {
      this.closeServiceDrawer({ clear: true })
   }

   saveService = async () => {
      await this.serviceSaveLoader.executeLoading(async () => {
         if (this.selectedServiceId) {
            await this.updateService()
         } else {
            await this.createService()
         }
      })
   }

   validatePricing = () => {
      const pricing = {
         startingPrice: Number(this.fields.startingPrice),
         startingPriceRate: this.fields.startingPriceRate,
      }
      const result = ServiceAdvisorCreateDtoSchema.shape.pricing
         .omit({ currency: true })
         .safeParse(pricing)
      if (!result.success) {
         result.error.errors.forEach((error) => {
            const fieldName = error.path[0].toString() as 'startingPrice' | 'startingPriceRate'
            this.formHandler[fieldName].addError(error.message)
         })
      }

      return result.success
   }

   createService = async () => {
      switch (this.currentOrg?.orgType) {
         case 'gap': {
            // These calls to validate() aren't validating pricing anymore, since the DTO has them with in the .pricing key
            const success = this.formHandler.validate(ServiceGapCreateDtoSchema)
            if (!success) return
            break
         }
         case 'advisor': {
            const serviceIsValid = this.formHandler.validate(ServiceAdvisorCreateDtoSchema)
            const pricingIsValid = this.validatePricing()
            if (!serviceIsValid || !pricingIsValid) return
            break
         }
      }

      const gapServiceId = this.selectedGapServiceId ? { gapServiceId: this.selectedGapServiceId } : {}

      const lastAddedService = await apiClient.services.create.mutate({
         ...this.parsedFieldValues, ...gapServiceId
      })
      if (!lastAddedService) return

      const lastAddedServiceWithProcess: ServiceDto = {
         ...notNull(lastAddedService),
      }
      let categoryFound = false
      this.serviceAndCategoryList = this.serviceAndCategoryList.map((categoryService) => {
         if (categoryService.category === this.fields.category) {
            categoryFound = true
            return {
               ...categoryService,
               services: [...categoryService.services, lastAddedServiceWithProcess],
            }
         }
         return categoryService
      })

      if (!categoryFound) {
         this.serviceAndCategoryList.push({ category: this.fields.category as ServiceCategoryTypeEnum, services: [lastAddedServiceWithProcess] })
      }

      this.lastAddedOrEditedServiceId = lastAddedService.id

      this.sortServiceAndCategoryList()
      snackbarSuccess('Service successfully added')
      this.closeServiceDrawer({ clear: true })
   }

   updateService = async () => {
      switch (this.currentOrg?.orgType) {
         case 'gap': {
            if (!this.formHandler.validate(ServiceGapUpdateDtoSchema)) return
            break
         }
         case 'advisor': {
            const serviceIsValid = this.formHandler.validate(ServiceAdvisorUpdateDtoSchema)
            const pricingIsValid = this.validatePricing()
            if (!serviceIsValid || !pricingIsValid) return
            break
         }
      }

      await apiClient.services.update.mutate({
         id: notNull(this.selectedServiceId),
         ...this.parsedFieldValues,
      })

      this.lastAddedOrEditedServiceId = notNull(this.selectedServiceId)

      // update the service
      const serviceToBeUpdated: ServiceDto = {
         ...notNull(this.services.find((s) => s.id === this.selectedServiceId)),
         ...this.parsedFieldValues,
      }
      const updatedList: ServiceList = []
      this.serviceAndCategoryList.forEach(({ category, services }) => {
         updatedList.push({
            category,
            services: [
               // Filter out old version of the service
               ...services.filter((s) => s.id !== this.selectedServiceId),
               // Add the new version of the service
               ...(category === this.parsedFieldValues.category ? [serviceToBeUpdated] : []),
            ],
         })
      })

      // Add the new category if it isn't already in the list
      if (!updatedList.find(({ category }) => category === this.parsedFieldValues.category)) {
         updatedList.push({
            category: this.parsedFieldValues.category,
            services: [serviceToBeUpdated],
         })
      }

      this.serviceAndCategoryList = updatedList

      this.sortServiceAndCategoryList()
      snackbarSuccess('Service successfully updated')
      this.closeServiceDrawer()
   }

   deleteService = async () => {
      this.serviceDeleteLoader.executeLoading(async () => {
         if (!this.selectedServiceId) {
            snackbarError('Unable to delete service')
            return
         }

         const serviceDeleted = await apiClient.services.delete.mutate({ serviceId: this.selectedServiceId })

         if (serviceDeleted) {
            this.serviceAndCategoryList.forEach(serviceCategory => {
               serviceCategory.services = serviceCategory.services.filter(service => service.id !== this.selectedServiceId)
            })

            this.serviceAndCategoryList = this.serviceAndCategoryList.map(serviceCategory => ({
               ...serviceCategory,
               services: serviceCategory.services.filter(service => service.id !== this.selectedServiceId)
            })).filter(serviceCategory => serviceCategory.services.length > 0)

            snackbarSuccess('Service successfully deleted')
            this.closeServiceDrawer()
         } else {
            snackbarError('Unable to delete service')
         }
      })
   }

   displayReallyDelete = () => {
      this.reallyDelete = true
   }

   sortServiceAndCategoryList = () => {
      const serviceAndCategoryList: ServiceList = []

      for (const category of serviceCategoryTypeEnums) {
         const services = this.serviceAndCategoryList.find(c => c.category === category)?.services
            .sort((a, b) => a.order - b.order)
         if (services?.length) {
            serviceAndCategoryList.push({ category, services })
         }
      }

      this.serviceAndCategoryList = [...serviceAndCategoryList]
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         const allServices = await apiClient.services.list.query()

         this.serviceAndCategoryList = []

         for (const category of serviceCategoryTypeEnums) {
            const services = allServices.filter(service => service.category === category).sort((a, b) => a.order - b.order)
            if (services.length) {
               this.serviceAndCategoryList.push({ category, services })
            }
         }
      })
   }

   onDragEnd = async (result: DropResult) => {
      const { destination } = result;

      // return if there's no destination
      if (!destination) {
         return
      }

      // find the dragged service
      const draggedService = this.services
         .find(service => service.id === result.draggableId)

      if (!draggedService) return

      // add the service to its new category
      this.serviceAndCategoryList = this.serviceAndCategoryList.map(categoryService => {
         const indexOfUpdatingService = categoryService.services.findIndex(({ id }) => id === draggedService.id)
         // Remove the service from its original category
         if (indexOfUpdatingService > -1) {
            const spliced = [...categoryService.services]
            spliced.splice(indexOfUpdatingService, 1)
            categoryService.services = spliced
         }

         if (categoryService.category === destination.droppableId as ServiceCategoryTypeEnum) {
            // Split the services by author org
            const { myOrgServices, otherOrgServices } = categoryService.services.reduce<ServicesSplitByOrg>((split, service) => {
               split[
                  service.orgId === this.currentOrg?.orgId
                     ? 'myOrgServices'
                     : 'otherOrgServices'
               ].push(service)
               return split
            }, {
               myOrgServices: [],
               otherOrgServices: [],
            })

            // Push this org's services, sorted by their new order
            const newServices = myOrgServices
               .sort((a, b) => a.order - b.order)
               .map((service, index) => ({
                  ...service,
                  order: destination.index < index
                     ? index
                     : index + 1,
               }))

            // Insert the dragged service
            newServices.splice(destination.index, 0, {
               ...draggedService,
               order: destination.index,
               category: categoryService.category,
            })

            // Add any services from other orgs
            newServices.push(...otherOrgServices)

            return {
               ...categoryService,
               services: newServices
            };
         }
         return categoryService
      })

      // remove any categories with no services
      this.serviceAndCategoryList = this.serviceAndCategoryList.filter(categoryService => categoryService.services.length > 0)

      await apiClient.services.reorder.mutate({
         serviceId: result.draggableId,
         category: destination.droppableId as ServiceCategoryTypeEnum,
         order: destination.index
      })
   }

   preconfiguredServiceField = ({ id }: ServiceOption) => {
      const service = notNull(this.services.find(s => s.id === id))
      this.selectedGapServiceId = service.id

      return this.fillFieldsForService(service)
   }

   serviceNameFieldChangeHandler = () => {
      if (this.fields.name.length === 0) {
         this.selectedGapServiceId = null
      }
      this.onInputChange()
   }
}

interface ServicesSplitByOrg {
   myOrgServices: ServiceDto[],
   otherOrgServices: ServiceDto[],
}

export const { Provider: ServiceStoreProvider, useProvidedStore: useServiceStore } =
   createStoreContext(ServiceStore)
